<template>
  <div
    class="blur-box"
    :class="{
      'blur-box-no-drag': simpleMode,
      'blur-box-selected': selected,
      'blur-box-unselected': !selected
    }"
    :style="boxStyle"
    @mousedown.stop="onBoxMouseDown"
  >
    <template v-if="selected">
      <div
        class="resize-handle resize-handle-nw"
        @mousedown.stop="startResize($event, 'nw')"
      ></div>
      <div
        class="resize-handle resize-handle-ne"
        @mousedown.stop="startResize($event, 'ne')"
      ></div>
      <div
        class="resize-handle resize-handle-sw"
        @mousedown.stop="startResize($event, 'sw')"
      ></div>
      <div
        class="resize-handle resize-handle-se"
        @mousedown.stop="startResize($event, 'se')"
      ></div>
    </template>
  </div>
</template>

<script>
import { clampBox } from "@/utils/blur";
import { mapGetters } from "vuex";

export default {
  name: "BlurBox",
  props: {
    box: { type: Object, required: true },
    selected: { type: Boolean, default: false },
    simpleMode: { type: Boolean, default: false },
    displayScale: { type: Number, required: true },
    imageDimensions: { type: Object, default: null },
    originalBox: { type: Object, default: null }
  },
  emits: ["select", "update"],
  data() {
    return {
      isDragging: false,
      isResizing: false,
      resizeDirection: null,
      startX: 0,
      startY: 0,
      startBoxX: 0,
      startBoxY: 0,
      startBoxWidth: 0,
      startBoxHeight: 0
    };
  },
  computed: {
    ...mapGetters({
      getBlurMaxBoxWidth: "bodyprint/getBlurMaxBoxWidth",
      getBlurMaxBoxHeight: "bodyprint/getBlurMaxBoxHeight",
      getBlurPositionRatio: "bodyprint/getBlurPositionRatio",
      getBlurSimpleResizeMargin: "bodyprint/getBlurSimpleResizeMargin"
    }),
    boxStyle() {
      const { box, displayScale: scale, imageDimensions: dim } = this;
      const paddingX = box.width * 0.4;
      const paddingY = box.height * 0.1;

      let paddedX = Math.max(0, box.x - paddingX);
      let paddedY = Math.max(0, box.y - paddingY);
      let paddedWidth = box.width + 2 * paddingX;
      let paddedHeight = box.height + 2 * paddingY;

      if (dim) {
        paddedWidth = Math.min(dim.width - paddedX, paddedWidth);
        paddedHeight = Math.min(dim.height - paddedY, paddedHeight);
      }

      return {
        left: paddedX * scale + "px",
        top: paddedY * scale + "px",
        width: paddedWidth * scale + "px",
        height: paddedHeight * scale + "px"
      };
    }
  },
  mounted() {
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);
  },
  beforeDestroy() {
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
  },
  methods: {
    onBoxMouseDown(event) {
      this.$emit("select");
      if (!this.selected) return;
      this.startDrag(event);
    },
    startDrag(event) {
      if (this.simpleMode) return;
      this.isDragging = true;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startBoxX = this.box.x;
      this.startBoxY = this.box.y;
      this.startBoxWidth = this.box.width;
      this.startBoxHeight = this.box.height;
      event.preventDefault();
    },
    startResize(event, direction) {
      this.isResizing = true;
      this.resizeDirection = direction;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startBoxX = this.box.x;
      this.startBoxY = this.box.y;
      this.startBoxWidth = this.box.width;
      this.startBoxHeight = this.box.height;
      event.preventDefault();
    },
    doClamp(box) {
      return clampBox(box, {
        imageDimensions: this.imageDimensions,
        isSimpleMode: this.simpleMode,
        originalBox: this.originalBox,
        simpleResizeMargin: this.getBlurSimpleResizeMargin,
        maxBoxWidth: this.getBlurMaxBoxWidth,
        maxBoxHeight: this.getBlurMaxBoxHeight,
        maxPositionRatio: this.getBlurPositionRatio
      });
    },
    onMouseMove(event) {
      if (!this.isDragging && !this.isResizing) return;

      const deltaX = (event.clientX - this.startX) / this.displayScale;
      const deltaY = (event.clientY - this.startY) / this.displayScale;
      let newBox;

      if (this.isDragging) {
        newBox = {
          x: this.startBoxX + deltaX,
          y: this.startBoxY + deltaY,
          width: this.startBoxWidth,
          height: this.startBoxHeight
        };
      } else if (this.isResizing) {
        const dir = this.resizeDirection;
        const sw = this.startBoxWidth;
        const sh = this.startBoxHeight;

        // Compute the visual edges of the START box (before any drag delta).
        // These are the anchors — the fixed side must never move.
        const startVisualLeft = this.startBoxX - 0.4 * sw;
        const startVisualRight = this.startBoxX + 1.4 * sw;
        const startVisualTop = this.startBoxY - 0.1 * sh;
        const startVisualBottom = this.startBoxY + 1.1 * sh;

        // Apply delta only to the dragged side; keep the opposite side fixed.
        let visualLeft = startVisualLeft;
        let visualRight = startVisualRight;
        let visualTop = startVisualTop;
        let visualBottom = startVisualBottom;

        if (dir.includes("w")) visualLeft = startVisualLeft + deltaX;
        if (dir.includes("e")) visualRight = startVisualRight + deltaX;
        if (dir.includes("n")) visualTop = startVisualTop + deltaY;
        if (dir.includes("s")) visualBottom = startVisualBottom + deltaY;

        // Convert visual extents back to the internal box (inverse of boxStyle padding).
        // visualWidth = 1.8w → w = visualWidth / 1.8
        // visualHeight = 1.2h → h = visualHeight / 1.2
        // x = visualLeft + 0.4w
        // y = visualTop + 0.1h
        const newWidth = Math.max(20, (visualRight - visualLeft) / 1.8);
        const newHeight = Math.max(20, (visualBottom - visualTop) / 1.2);

        // Re-anchor the fixed side so it can't drift when width/height hits the min clamp.
        let newX, newY;
        if (dir.includes("w")) {
          // the fixed side is right: x + 1.4w = startVisualRight → x = startVisualRight - 1.4w
          newX = startVisualRight - 1.4 * newWidth;
        } else {
          // the fixed side is left: x - 0.4w = startVisualLeft → x = startVisualLeft + 0.4w
          newX = startVisualLeft + 0.4 * newWidth;
        }
        if (dir.includes("n")) {
          // the fixed side is bottom: y + 1.1h = startVisualBottom  →  y = startVisualBottom - 1.1h
          newY = startVisualBottom - 1.1 * newHeight;
        } else {
          // the fixed side is top: y - 0.1h = startVisualTop → y = startVisualTop + 0.1h
          newY = startVisualTop + 0.1 * newHeight;
        }

        newBox = { x: newX, y: newY, width: newWidth, height: newHeight };
      }

      if (newBox) {
        let clamped = this.doClamp(newBox);

        if (this.isResizing) {
          const dir = this.resizeDirection;
          const sw = this.startBoxWidth;
          const sh = this.startBoxHeight;

          // After clamping, the fixed side may have drifted. Restore it.
          if (!dir.includes("w")) {
            // Left is fixed: x - 0.4w = startVisualLeft → x = startVisualLeft + 0.4w
            const startVisualLeft = this.startBoxX - 0.4 * sw;
            clamped.x = Math.round(startVisualLeft + 0.4 * clamped.width);
          }
          if (!dir.includes("e")) {
            // Right is fixed: x + 1.4w = startVisualRight → x = startVisualRight - 1.4w
            const startVisualRight = this.startBoxX + 1.4 * sw;
            clamped.x = Math.round(startVisualRight - 1.4 * clamped.width);
          }
          if (!dir.includes("n")) {
            // Top is fixed: y - 0.1h = startVisualTop → y = startVisualTop + 0.1h
            const startVisualTop = this.startBoxY - 0.1 * sh;
            clamped.y = Math.round(startVisualTop + 0.1 * clamped.height);
          }
          if (!dir.includes("s")) {
            // Bottom is fixed: y + 1.1h = startVisualBottom → y = startVisualBottom - 1.1h
            const startVisualBottom = this.startBoxY + 1.1 * sh;
            clamped.y = Math.round(startVisualBottom - 1.1 * clamped.height);
          }
        }

        this.$emit("update", clamped);
      }
    },
    onMouseUp() {
      this.isDragging = false;
      this.isResizing = false;
      this.resizeDirection = null;
    }
  }
};
</script>

<style scoped>
.blur-box {
  position: absolute;
  box-sizing: border-box;
  z-index: 10;
}

.blur-box-unselected {
  border: 1px dashed rgba(255, 107, 107, 0.6);
  background-color: rgba(255, 107, 107, 0.1);
  cursor: pointer;
}

.blur-box-selected {
  border: 2px solid #ff6b6b;
  background-color: rgba(255, 107, 107, 0.3);
  cursor: move;
}

.blur-box-no-drag {
  cursor: default;
}

.resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background-color: #ff6b6b;
  border: 1px solid white;
  border-radius: 2px;
  z-index: 11;
}

.resize-handle-nw {
  top: -6px;
  left: -6px;
  cursor: nw-resize;
}

.resize-handle-ne {
  top: -6px;
  right: -6px;
  cursor: ne-resize;
}

.resize-handle-sw {
  bottom: -6px;
  left: -6px;
  cursor: sw-resize;
}

.resize-handle-se {
  bottom: -6px;
  right: -6px;
  cursor: se-resize;
}
</style>
